import React, { useCallback, useEffect, useRef, useState } from "react";

/**
 * The right-hand detail panel's frame, shared by the asset drawers.
 *
 * It exists so a row click can open the panel *immediately* and fill it in when the fetch lands.
 * Both inventory panels used to await the detail request and only then set state, so a click
 * changed nothing on screen for as long as the request took — and people clicked the row again,
 * and again, each click starting another request. The loading and failure states now occupy exactly
 * the panel the loaded drawer will, so nothing jumps when the data arrives.
 *
 * <p><b>Resizable.</b> The panel was a fixed 512px, which is fine for stacked text and far too
 * narrow for the identity Path graph — a chain of six stages had about 200px of canvas and could
 * not be read. Drag the left edge to widen it; double-click the edge to go back to the default.
 * The width is remembered per viewer.</p>
 *
 * `testId` marks the LOADED drawer, so a test can wait for real content rather than for the panel.
 */

const DEFAULT_WIDTH = 512;   // max-w-lg, the width this panel has always had
const MIN_WIDTH = 360;       // below this the stacked text starts wrapping badly
const EDGE_GAP = 80;         // always leave some backdrop visible to click away on
const WIDTH_KEY = "axaris.drawerWidth";

/** Per-viewer convenience only; a blocked or cleared store costs the preference, never the drawer. */
function storedWidth() {
  try {
    const raw = Number(window.localStorage.getItem(WIDTH_KEY));
    return Number.isFinite(raw) && raw >= MIN_WIDTH ? raw : DEFAULT_WIDTH;
  } catch {
    return DEFAULT_WIDTH;
  }
}

function rememberWidth(width) {
  try {
    window.localStorage.setItem(WIDTH_KEY, String(width));
  } catch {
    /* preference only */
  }
}

/**
 * Where the app's top menu (with its organization strip) ends on screen, so the panel starts below
 * it and both stay visible. Measured, not hard-coded: the menu's height changes with the window
 * width, and on a page without the pinned menu it scrolls away, at which point the panel can use
 * the full height.
 */
function menuBottom() {
  const menu = document.querySelector("[data-app-nav-shell]") || document.querySelector("[data-app-nav]");
  if (!menu) return 0;
  return Math.max(0, Math.round(menu.getBoundingClientRect().bottom));
}

const maxWidth = () => Math.max(MIN_WIDTH, (window.innerWidth || DEFAULT_WIDTH) - EDGE_GAP);
const clampWidth = (width) => Math.min(Math.max(width, MIN_WIDTH), maxWidth());

export default function DrawerShell({ title, subtitle, onClose, children, label = "Details", testId }) {
  const [width, setWidth] = useState(() => clampWidth(storedWidth()));
  const [top, setTop] = useState(menuBottom);

  // Follow the menu: a resize can change its height, and a page scroll moves it off screen.
  useEffect(() => {
    const follow = () => setTop(menuBottom());
    window.addEventListener("resize", follow);
    window.addEventListener("scroll", follow, { passive: true });
    return () => {
      window.removeEventListener("resize", follow);
      window.removeEventListener("scroll", follow);
    };
  }, []);

  const apply = useCallback((next) => {
    const clamped = clampWidth(next);
    setWidth(clamped);
    return clamped;
  }, []);

  // A window narrower than the remembered width would push the close button off screen.
  useEffect(() => {
    const onResize = () => setWidth((current) => clampWidth(current));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Move and release are tracked on the WINDOW, not on the handle. The handle is 6px wide and a
  // real drag outruns it immediately, so listening on the element alone would drop the gesture the
  // moment the pointer got ahead of the edge.
  const [dragging, setDragging] = useState(false);
  const guideRef = useRef(null);

  // The panel is NOT resized while the pointer moves. A drag used to re-render the panel on every
  // frame, and anything inside it that observes its own box — React Flow, which the identity Path
  // graph is built on — is then measured against a size that is changing continuously. The browser
  // reports that as "ResizeObserver loop completed with undelivered notifications", which is an
  // informational notice but one CRA's dev overlay escalates to a full-screen error.
  //
  // So the drag moves a guide line instead, written straight to the DOM with no React state, and
  // the width is committed ONCE on release. Nothing inside the panel is resized until the gesture
  // ends, which removes the condition rather than papering over the message.
  useEffect(() => {
    if (!dragging) return undefined;

    const widthAt = (clientX) => (window.innerWidth || DEFAULT_WIDTH) - clientX;
    const guideX = (clientX) => (window.innerWidth || DEFAULT_WIDTH) - clampWidth(widthAt(clientX));
    // Tracked explicitly rather than inferred from the release coordinate: a pointerup always
    // carries one (the press position), so "did the pointer actually move" cannot be read off it.
    let moved = false;
    let last = null;

    const onMove = (event) => {
      if (event.clientX == null) return;
      moved = true;
      last = event.clientX;
      // Direct DOM write: no setState, so nothing inside the drawer re-renders mid-drag.
      if (guideRef.current) guideRef.current.style.left = `${guideX(event.clientX)}px`;
    };

    const onUp = () => {
      setDragging(false);
      if (!moved) return;              // a press with no movement changes nothing
      rememberWidth(apply(widthAt(last)));
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    // Dragging over text would otherwise select it across the whole page.
    const previousSelect = document.body.style.userSelect;
    document.body.style.userSelect = "none";

    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      document.body.style.userSelect = previousSelect;
    };
  }, [dragging, apply]);

  const onPointerDown = useCallback((event) => {
    event.preventDefault();          // no text selection while dragging the edge
    setDragging(true);
  }, []);

  // Keyboard, because a drag handle that only takes a mouse is not a control everyone has.
  const onKeyDown = useCallback((event) => {
    const step = event.shiftKey ? 100 : 20;
    if (event.key === "ArrowLeft") rememberWidth(apply(width + step));
    else if (event.key === "ArrowRight") rememberWidth(apply(width - step));
    else return;
    event.preventDefault();
  }, [apply, width]);

  const reset = useCallback(() => rememberWidth(apply(DEFAULT_WIDTH)), [apply]);

  return (
    // Starts at the menu's bottom edge rather than the top of the window: at top 0 the menu (z-50)
    // covered the panel's first lines — the account name and the start of the detail. Kept under
    // the menu's layer so the menu's own dropdowns still open over the panel.
    <div className="fixed inset-x-0 bottom-0 z-40 flex" style={{ top }} role="dialog" aria-modal="true" aria-label={label} data-testid={testId}>
      <div className="flex-1 bg-black/20" onClick={onClose} />

      {/* Where the edge will land. Only this moves during the drag; the panel itself is resized
          once, on release, so nothing inside it is measured against a size still in motion. */}
      {dragging && (
        <div
          ref={guideRef}
          data-testid="drawer-resize-guide"
          aria-hidden="true"
          className="fixed bottom-0 w-0.5 bg-indigo-400 z-50 pointer-events-none"
          style={{ top, left: (window.innerWidth || DEFAULT_WIDTH) - width }}
        />
      )}

      {/* The handle sits OUTSIDE the backdrop, so grabbing it never closes the drawer. */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize panel"
        aria-valuenow={Math.round(width)}
        aria-valuemin={MIN_WIDTH}
        aria-valuemax={Math.round(maxWidth())}
        tabIndex={0}
        data-testid="drawer-resize-handle"
        title="Drag to resize · double-click to reset"
        onPointerDown={onPointerDown}
        onDoubleClick={reset}
        onKeyDown={onKeyDown}
        className="w-1.5 shrink-0 cursor-col-resize bg-gray-200 hover:bg-indigo-400 focus:bg-indigo-400 focus:outline-none touch-none"
      />

      {/*
        `scrollbar-gutter: stable` reserves the scrollbar's track whether or not it is showing.
        Without it, NARROWING the panel is a feedback loop: less width makes the content taller, the
        vertical scrollbar appears, that takes ~15px off the inner width, the content reflows again,
        and the scrollbar may no longer be needed. Anything inside measuring its own box then never
        settles — which is why the error appeared on shrinking and never on growing.
      */}
      <div className="bg-white h-full overflow-y-auto shadow-xl p-5"
           style={{ width, maxWidth: "100vw", scrollbarGutter: "stable" }}>
        <div className="flex items-start justify-between gap-2 mb-3">
          <div>
            <div className="text-sm font-semibold text-gray-900">{title}</div>
            {subtitle && <div className="text-xs text-gray-500">{subtitle}</div>}
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-700">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

/**
 * The same detail content laid out in the page instead of over it: a bordered panel with the title,
 * subtitle and Close, for pages that expand details in place (Security Operations). Takes
 * DrawerShell's props, so a detail view can render in either.
 */
export function InlineDetailShell({ title, subtitle, onClose, children, label = "Details", testId }) {
  return (
    <section aria-label={label} data-testid={testId} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div>
          <div className="text-sm font-semibold text-gray-900">{title}</div>
          {subtitle && <div className="text-xs text-gray-500">{subtitle}</div>}
        </div>
        <button type="button" onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-700">✕</button>
      </div>
      {children}
    </section>
  );
}

/**
 * What the panel shows between the click and the response. Deliberately shaped like the content it
 * replaces, and it states what is happening in words as well as in skeleton bars.
 */
export function DrawerLoading({ testId = "detail-loading", message = "Loading details…" }) {
  return (
    <div className="space-y-2" data-testid={testId} aria-busy="true">
      <div className="h-3 w-40 rounded bg-gray-100 animate-pulse" />
      <div className="h-3 w-64 rounded bg-gray-100 animate-pulse" />
      <div className="h-3 w-52 rounded bg-gray-100 animate-pulse" />
      <div className="text-xs text-gray-400 pt-1">{message}</div>
    </div>
  );
}

/**
 * A failed open. Silence here looks exactly like a click that never registered, which is the other
 * half of why people click repeatedly — so it says what happened and offers the retry.
 */
export function DrawerFailed({ onRetry, testId = "detail-failed", message = "The details could not be loaded." }) {
  return (
    <div className="text-xs text-gray-600" data-testid={testId}>
      <p>{message}</p>
      <button type="button" onClick={onRetry}
        className="mt-2 px-2 py-1 rounded border border-gray-300 hover:bg-gray-50">Try again</button>
    </div>
  );
}
